import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import ConfirmationBox from '../../Components/Confirmation/ConfirmationBox';
import api from '../../api';
import { toast } from 'react-toastify';
import './pipeline.css';

const POLL_MS = 15000;
const LOG_POLL_MS = 30000;

const COLUMN_META = {
  running: { label: 'Running', accent: 'running' },
  queued: { label: 'Queued', accent: 'queued' },
  awaiting: { label: 'Awaiting user', accent: 'awaiting' },
  failed: { label: 'Failed / stuck', accent: 'failed' },
};

function Metric({ label, value, warn, danger, hint }) {
  const cls = ['pipeline-metric', warn && 'pipeline-metric--warn', danger && 'pipeline-metric--danger']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="pipeline-metric__label">{label}</div>
      <div className="pipeline-metric__value">{value ?? '—'}</div>
      {hint && <div className="pipeline-metric__hint">{hint}</div>}
    </div>
  );
}

function JobTypeBadge({ type }) {
  const labels = {
    dreamface: 'Talking Heads',
    standard: 'Standard',
    regenerate: 'Regenerate',
    video: 'Video',
  };
  return (
    <span className={`pipeline-badge pipeline-badge--${type || 'standard'}`}>
      {labels[type] || type || 'video'}
    </span>
  );
}

function formatUser(email, names) {
  if (!email) return '—';
  return names?.[email] ? `${names[email]}` : email;
}

function formatDuration(sec) {
  if (!sec && sec !== 0) return '—';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatCardDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function sortBoardItems(items, columnKey) {
  const list = [...(items || [])];
  if (columnKey === 'queued') {
    return list.sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
  }
  return list.sort((a, b) => {
    const ta = a.sort_at || a.created_at || '';
    const tb = b.sort_at || b.created_at || '';
    return tb.localeCompare(ta);
  });
}

function HealthBanner({ health }) {
  if (!health) return null;
  const level = health.level || 'healthy';
  return (
    <div className={`pipeline-health pipeline-health--${level}`} role="status">
      <span className="pipeline-health__label">{health.label}</span>
      <span className="pipeline-health__message">{health.message}</span>
    </div>
  );
}

function SummaryStrip({ summary }) {
  if (!summary) return null;
  return (
    <div className="pipeline-summary">
      <div className="pipeline-summary__item pipeline-summary__item--running">
        <span className="pipeline-summary__count">{summary.running ?? 0}</span>
        <span className="pipeline-summary__label">Running</span>
      </div>
      <div className="pipeline-summary__item pipeline-summary__item--queued">
        <span className="pipeline-summary__count">{summary.queued ?? 0}</span>
        <span className="pipeline-summary__label">Queued</span>
      </div>
      <div className="pipeline-summary__item pipeline-summary__item--awaiting">
        <span className="pipeline-summary__count">{summary.awaiting ?? 0}</span>
        <span className="pipeline-summary__label">Awaiting user</span>
      </div>
      <div className="pipeline-summary__item pipeline-summary__item--failed">
        <span className="pipeline-summary__count">{summary.failed ?? 0}</span>
        <span className="pipeline-summary__label">Failed</span>
      </div>
      <div className="pipeline-summary__divider" />
      <div className="pipeline-summary__item">
        <span className="pipeline-summary__count">
          {summary.active_video_slots ?? 0}/{summary.video_slot_limit ?? '?'}
        </span>
        <span className="pipeline-summary__label">Video slots</span>
      </div>
      <div className="pipeline-summary__item">
        <span className="pipeline-summary__count">
          {summary.segment_slots_used ?? 0}/{summary.segment_slots_cap ?? '?'}
        </span>
        <span className="pipeline-summary__label">Segment slots</span>
      </div>
    </div>
  );
}

function resolveJobLink(item) {
  if (!item) return null;
  return (
    item.link
    || item.dreamface_link
    || item.compose_link
    || (item.dreamface_job_id ? `/dreamface/${item.dreamface_job_id}` : null)
    || (item.vid_data_id ? `/compose/${item.vid_data_id}` : null)
    || (item.ref_id && item.job_type === 'dreamface' ? `/dreamface/${item.ref_id}` : null)
    || (item.ref_id ? `/compose/${item.ref_id}` : null)
  );
}

function BoardCard({ item, names, acting, onMove, onDreamface, onConfirm, queueLength }) {
  const isQueued = item.column === 'queued';
  const openLink = resolveJobLink(item);
  const showCompose =
    item.compose_link && openLink && item.compose_link !== openLink;
  const showDreamface =
    item.dreamface_link && openLink && item.dreamface_link !== openLink;

  return (
    <article
      className={`pipeline-board-card ${item.stuck ? 'pipeline-board-card--stuck' : ''}`}
    >
      <div className="pipeline-board-card__head">
        <JobTypeBadge type={item.job_type} />
        <div className="pipeline-board-card__head-meta">
          {item.created_at && (
            <time className="pipeline-board-card__date" dateTime={item.created_at}>
              {formatCardDate(item.created_at)}
            </time>
          )}
          {item.runtime_seconds != null && (
            <span className="pipeline-board-card__time">
              {isQueued ? 'wait ' : ''}
              {formatDuration(item.runtime_seconds)}
            </span>
          )}
        </div>
      </div>
      <h3 className="pipeline-board-card__title">{item.title}</h3>
      <p className="pipeline-board-card__phase">{item.phase_label}</p>
      <p className="pipeline-board-card__user">{formatUser(item.user_id, names)}</p>
      {item.error && (
        <p className="pipeline-board-card__error" title={item.error}>
          {item.error}
        </p>
      )}
      {item.stuck && <span className="pipeline-board-card__stuck-tag">Likely stuck</span>}
      <div className="pipeline-board-card__actions">
        {openLink && (
          <Link to={openLink} className="pipeline-btn pipeline-btn--xs pipeline-btn--ghost">
            Open
          </Link>
        )}
        {showDreamface && (
          <Link
            to={item.dreamface_link}
            className="pipeline-btn pipeline-btn--xs pipeline-btn--ghost"
          >
            Talking Heads
          </Link>
        )}
        {showCompose && (
          <Link
            to={item.compose_link}
            className="pipeline-btn pipeline-btn--xs pipeline-btn--ghost"
          >
            Compose
          </Link>
        )}
        {isQueued && item.queue_id && (
          <>
            <button
              type="button"
              className="pipeline-btn pipeline-btn--xs pipeline-btn--ghost"
              disabled={acting}
              onClick={() => onMove(item.queue_id, 'up')}
              title="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              className="pipeline-btn pipeline-btn--xs pipeline-btn--ghost"
              disabled={acting}
              onClick={() => onMove(item.queue_id, 'down')}
              title="Move down"
            >
              ↓
            </button>
          </>
        )}
        {item.dreamface_job_id &&
          (item.status === 'processing' || item.status === 'failed') && (
            <button
              type="button"
              className="pipeline-btn pipeline-btn--xs pipeline-btn--primary"
              disabled={acting}
              onClick={() => onDreamface(item.dreamface_job_id, 'retry')}
            >
              Retry
            </button>
          )}
        {item.dreamface_job_id && item.status === 'processing' && (
          <button
            type="button"
            className="pipeline-btn pipeline-btn--xs pipeline-btn--secondary"
            disabled={acting}
            onClick={() =>
              onConfirm({
                title: 'Reset DreamFace job',
                message: 'Moves job back to awaiting avatars so links can be edited.',
                onConfirm: () => onDreamface(item.dreamface_job_id, 'reset'),
              })
            }
          >
            Reset
          </button>
        )}
      </div>
    </article>
  );
}

function BoardColumn({ columnKey, items, names, acting, onMove, onDreamface, onConfirm, queueLength }) {
  const meta = COLUMN_META[columnKey];
  const sortedItems = sortBoardItems(items, columnKey);
  return (
    <section className={`pipeline-board-col pipeline-board-col--${meta.accent}`}>
      <header className="pipeline-board-col__head">
        <h2>{meta.label}</h2>
        <span className="pipeline-board-col__count">{sortedItems.length}</span>
      </header>
      <div className="pipeline-board-col__body">
        {sortedItems.length === 0 ? (
          <p className="pipeline-board-col__empty">Nothing here</p>
        ) : (
          sortedItems.map((item) => (
            <BoardCard
              key={item.id}
              item={item}
              names={names}
              acting={acting}
              onMove={onMove}
              onDreamface={onDreamface}
              onConfirm={onConfirm}
              queueLength={queueLength}
            />
          ))
        )}
      </div>
    </section>
  );
}

function ActivityFeed({ activity }) {
  if (!activity?.length) {
    return <p className="pipeline-empty">No recent activity yet — completions appear here.</p>;
  }

  const iconFor = (type) => {
    if (type === 'completed') return '✓';
    if (type === 'failed') return '✕';
    if (type === 'started') return '▶';
    return '•';
  };

  return (
    <ul className="pipeline-activity">
      {activity.map((ev, i) => (
        <li
          key={`${ev.at}-${i}`}
          className={`pipeline-activity__item pipeline-activity__item--${ev.type || ev.kind}`}
        >
          <span className="pipeline-activity__icon">{iconFor(ev.type)}</span>
          <div className="pipeline-activity__body">
            <div className="pipeline-activity__row">
              <strong>{ev.title || 'Job'}</strong>
              <JobTypeBadge type={ev.job_type} />
            </div>
            <p className="pipeline-activity__meta">
              {ev.user_name} · {ev.message}
              {ev.duration_seconds != null && ` · ${formatDuration(ev.duration_seconds)}`}
            </p>
            <time className="pipeline-activity__time">{formatTime(ev.at)}</time>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PipelineControl() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [actionLog, setActionLog] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('board');

  const pushLog = useCallback((message) => {
    const entry = { ts: new Date().toISOString(), message };
    setActionLog((prev) => [entry, ...prev].slice(0, 50));
  }, []);

  const fetchOverview = useCallback(async (silent = false, withLogs = false) => {
    if (!silent) setLoading(true);
    try {
      const params = withLogs ? { logs: '1' } : {};
      const res = await api.get('/pipeline/admin/status', { params });
      setOverview(res.data);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to load pipeline status';
      if (!silent) toast.error(msg);
      pushLog(`ERROR overview: ${msg}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pushLog]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchOverview(true, true);
    }
  }, [activeTab, fetchOverview]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const withLogs = activeTab === 'logs';
    const intervalMs = withLogs ? LOG_POLL_MS : POLL_MS;
    const id = setInterval(() => fetchOverview(true, withLogs), intervalMs);
    return () => clearInterval(id);
  }, [autoRefresh, fetchOverview, activeTab]);

  const applyOverview = (data) => {
    if (data?.overview) setOverview(data.overview);
    else if (data?.after) setOverview((prev) => ({ ...prev, ...data.after }));
  };

  const runAction = async (endpoint, actionLabel, body = {}) => {
    setActing(true);
    try {
      const res = await api.post(endpoint, body);
      applyOverview(res.data);
      if (res.data?.after) {
        pushLog(
          `${actionLabel} OK — slots ${res.data.before?.global_in_flight ?? '?'} → ${res.data.after?.global_in_flight ?? '?'}`
        );
      } else {
        pushLog(`${actionLabel} OK`);
      }
      toast.success(`${actionLabel} completed`);
      await fetchOverview(true);
    } catch (err) {
      const msg = err?.response?.data?.error || `${actionLabel} failed`;
      toast.error(msg);
      pushLog(`ERROR ${actionLabel}: ${msg}`);
    } finally {
      setActing(false);
    }
  };

  const moveQueue = async (queueId, direction) => {
    setActing(true);
    try {
      const res = await api.post('/pipeline/admin/queue/move', {
        queue_id: queueId,
        direction,
      });
      setOverview(res.data.overview);
      pushLog(`Queue moved ${direction} · ${queueId}`);
      toast.success(`Job moved ${direction}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not move job');
    } finally {
      setActing(false);
    }
  };

  const adminDreamfaceAction = async (jobId, action) => {
    setActing(true);
    try {
      const endpoint =
        action === 'retry'
          ? `/pipeline/admin/dreamface/${jobId}/retry`
          : `/pipeline/admin/dreamface/${jobId}/reset`;
      const res = await api.post(endpoint, action === 'retry' ? { force: true } : {});
      if (res.data?.overview) setOverview(res.data.overview);
      pushLog(`DreamFace ${action} · ${jobId.slice(-8)}`);
      toast.success(`DreamFace job ${action} OK`);
      await fetchOverview(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || `DreamFace ${action} failed`);
    } finally {
      setActing(false);
    }
  };

  const board = overview?.board;
  const names = overview?.user_names || {};
  const columns = board?.columns || {};
  const queued = overview?.queued_video_jobs || [];
  const serverLogs = overview?.server_logs || [];

  const summaryTotal =
    (board?.summary?.running ?? 0) +
    (board?.summary?.queued ?? 0) +
    (board?.summary?.awaiting ?? 0) +
    (board?.summary?.failed ?? 0);

  const tabs = [
    { id: 'board', label: `Board (${summaryTotal})` },
    { id: 'ops', label: 'System & recovery' },
    { id: 'logs', label: 'Logs' },
  ];

  const stallSec = overview?.stall_seconds ?? 0;
  const stallLimit = overview?.stall_unstick_sec ?? 300;
  const slotsUsed = overview?.global_in_flight ?? 0;
  const slotsCap = overview?.global_in_flight_cap ?? 0;
  const slotsFull = slotsCap > 0 && slotsUsed >= slotsCap;
  const stalled = stallSec >= stallLimit && slotsUsed > 0;

  return (
    <div className="pipeline-control">
      <Header />
      <div className="pipeline-admin">
        <div className="pipeline-admin__header">
          <div>
            <h1>Pipeline manager</h1>
            <p>
              Mission control for video production — every running job, queue position, Talking
              Heads state, and completion in one live board.
              {board?.max_age_days != null && (
                <span className="pipeline-admin__scope">
                  {' '}
                  Showing jobs from the last {board.max_age_days} days only.
                </span>
              )}
            </p>
          </div>
          {overview?.generated_at && (
            <time className="pipeline-admin__updated">
              Updated {new Date(overview.generated_at).toLocaleTimeString()}
            </time>
          )}
        </div>

        <HealthBanner health={board?.health} />

        {stalled && board?.health?.level !== 'critical' && (
          <div className="pipeline-stale-banner">
            Pipeline appears stalled: {Math.round(stallSec)}s since last segment progress with{' '}
            {slotsUsed}/{slotsCap} slots held.
          </div>
        )}

        <div className="pipeline-admin__toolbar">
          <button
            type="button"
            className="pipeline-btn pipeline-btn--ghost"
            onClick={() => fetchOverview()}
            disabled={loading || acting}
          >
            Refresh now
          </button>
          <label className="pipeline-checkbox">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Live refresh ({POLL_MS / 1000}s{activeTab === 'logs' ? ` · logs ${LOG_POLL_MS / 1000}s` : ''})
          </label>
          {loading && <span className="pipeline-muted">Loading…</span>}
        </div>

        <SummaryStrip summary={board?.summary} />

        <div className="pipeline-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pipeline-tab ${activeTab === tab.id ? 'pipeline-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'board' && (
          <>
            <div className="pipeline-board">
              {['running', 'queued', 'awaiting', 'failed'].map((col) => (
                <BoardColumn
                  key={col}
                  columnKey={col}
                  items={columns[col] || []}
                  names={names}
                  acting={acting}
                  onMove={moveQueue}
                  onDreamface={adminDreamfaceAction}
                  onConfirm={setConfirm}
                  queueLength={queued.length}
                />
              ))}
            </div>

            <section className="pipeline-section pipeline-section--activity">
              <h2>Recent activity</h2>
              <p className="pipeline-section__hint">
                Job starts, completions, failures, and saved videos — newest first.
              </p>
              <ActivityFeed activity={board?.activity} />
            </section>
          </>
        )}

        {activeTab === 'ops' && (
          <>
            <div className="pipeline-admin__grid">
              <Metric
                label="Active videos"
                value={`${overview?.active_video_jobs ?? 0} / ${overview?.concurrent_video_jobs_limit ?? '?'}`}
              />
              <Metric
                label="Global slots"
                value={`${slotsUsed} / ${slotsCap}`}
                warn={slotsFull}
                danger={stalled}
              />
              <Metric
                label="Stall timer"
                value={formatDuration(stallSec)}
                warn={stallSec > 120}
                danger={stalled}
                hint={`Auto-unstick after ${stallLimit}s`}
              />
              <Metric label="Pool workers" value={overview?.segment_workers} />
              <Metric label="Pool generation" value={overview?.pool_generation} />
              <Metric label="Script queue" value={overview?.script_queue_depth} />
              <Metric label="Storyboard slots" value={overview?.storyboard_slots} />
              <Metric label="GPU slots" value={overview?.gpu_slots} />
              <Metric
                label="Segment wall timeout"
                value={`${overview?.segment_wall_timeout_sec ?? '?'}s`}
              />
            </div>

            <div className="pipeline-admin__actions">
              <button
                type="button"
                className="pipeline-btn pipeline-btn--danger"
                disabled={acting}
                onClick={() =>
                  setConfirm({
                    title: 'Force recovery',
                    message:
                      'Clears global slot counters, recreates worker pool, resets GPU/storyboard semaphores. Use when frozen with idle CPU.',
                    onConfirm: () => {
                      setConfirm(null);
                      runAction('/pipeline/admin/recover', 'Force recovery', {
                        reason: 'admin UI force recovery',
                      });
                    },
                  })
                }
              >
                Force recovery
              </button>
              <button
                type="button"
                className="pipeline-btn pipeline-btn--primary"
                disabled={acting}
                onClick={() =>
                  setConfirm({
                    title: 'Recreate worker pool',
                    message: 'Kills and respawns all segment worker processes.',
                    onConfirm: () => {
                      setConfirm(null);
                      runAction('/pipeline/admin/recreate-pool', 'Recreate pool', {
                        reason: 'admin UI recreate pool',
                      });
                    },
                  })
                }
              >
                Recreate pool
              </button>
              <button
                type="button"
                className="pipeline-btn pipeline-btn--secondary"
                disabled={acting}
                onClick={() =>
                  setConfirm({
                    title: 'Reset semaphores',
                    message: 'Resets GPU and storyboard IPC locks only.',
                    onConfirm: () => {
                      setConfirm(null);
                      runAction('/pipeline/admin/reset-semaphores', 'Reset semaphores', {
                        reason: 'admin UI reset semaphores',
                      });
                    },
                  })
                }
              >
                Reset GPU locks
              </button>
            </div>
          </>
        )}

        {activeTab === 'logs' && (
          <section className="pipeline-section">
            <h2>Server logs</h2>
            {!overview?.log_file_configured ? (
              <div className="pipeline-log-notice">
                Set <code>PIPELINE_LOG_FILE</code> on the backend (e.g.{' '}
                <code>logs/backend.log</code>) and configure your process to write logs there.
              </div>
            ) : (
              <div className="pipeline-server-log">
                {serverLogs.length === 0 ? (
                  <p className="pipeline-empty">Log file is empty or unreadable.</p>
                ) : (
                  serverLogs.map((line, i) => (
                    <div
                      key={`log-${i}`}
                      className={`pipeline-log-line ${
                        /error|❌|failed|stall|recovery/i.test(line)
                          ? 'pipeline-log-line--err'
                          : ''
                      } ${/warn|⚠|skipped/i.test(line) ? 'pipeline-log-line--warn' : ''}`}
                    >
                      {line}
                    </div>
                  ))
                )}
              </div>
            )}

            <h3 className="pipeline-section__subhead">Admin action log (this session)</h3>
            <div className="pipeline-admin__log">
              {actionLog.length === 0 && (
                <div className="pipeline-log-entry">No admin actions yet this session.</div>
              )}
              {actionLog.map((entry, i) => (
                <div key={`${entry.ts}-${i}`} className="pipeline-log-entry">
                  <time>{new Date(entry.ts).toLocaleTimeString()}</time>
                  {entry.message}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {confirm && (
        <ConfirmationBox
          message={`${confirm.title}: ${confirm.message}`}
          handleConfirm={(ok) => {
            if (ok) confirm.onConfirm();
            else setConfirm(null);
          }}
        />
      )}
    </div>
  );
}

export default PipelineControl;
